import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Auth API
export const authApi = {
  forgotPassword: (email) => axios.post(`${API}/auth/forgot-password`, { email }),
  resetPassword: (token, new_password) => axios.post(`${API}/auth/reset-password`, { token, new_password }),
  changePassword: (current_password, new_password) =>
    axios.post(`${API}/auth/change-password`, { current_password, new_password }, { headers: getAuthHeader() }),
};

// Clock In/Out API
export const clockApi = {
  clockIn: (body) => axios.post(`${API}/clock/in`, body || {}, { headers: getAuthHeader() }),
  clockOut: () => axios.post(`${API}/clock/out`, {}, { headers: getAuthHeader() }),
  getStatus: () => axios.get(`${API}/clock/status`, { headers: getAuthHeader() }),
  addClientTime: (recordId, clientId) => axios.post(`${API}/clock/${recordId}/client-time?client_id=${clientId}`, {}, { headers: getAuthHeader() }),
  getHistory: () => axios.get(`${API}/clock/history`, { headers: getAuthHeader() }),
  getClientGoals: (clientId) => axios.get(`${API}/clock/client-goals?client_id=${clientId}`, { headers: getAuthHeader() }),
  gpsPing: (pings) => axios.post(`${API}/clock/gps-ping`, { pings }, { headers: getAuthHeader() }),
  getLatestCompleted: () => axios.get(`${API}/clock/latest-completed`, { headers: getAuthHeader() }),
};

// Training API
export const trainingApi = {
  listModules: () => axios.get(`${API}/training/modules`, { headers: getAuthHeader() }),
  getProgress: () => axios.get(`${API}/training/progress`, { headers: getAuthHeader() }),
  complete: (moduleId, notes) => axios.post(`${API}/training/complete`, { module_id: moduleId, notes }, { headers: getAuthHeader() }),
  resetCompletion: (moduleId) => axios.delete(`${API}/training/complete/${moduleId}`, { headers: getAuthHeader() }),
};

// SOS API
export const sosApi = {
  trigger: (data) => axios.post(`${API}/sos`, data, { headers: getAuthHeader() }),
  active: () => axios.get(`${API}/sos/active`, { headers: getAuthHeader() }),
  list: () => axios.get(`${API}/sos`, { headers: getAuthHeader() }),
  acknowledge: (id) => axios.post(`${API}/sos/${id}/acknowledge`, {}, { headers: getAuthHeader() }),
};

// Photo Upload API
export const photosApi = {
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/photos/upload`, formData, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
    });
  },
  getUrl: (photoId) => `${API}/photos/${photoId}?token=${localStorage.getItem('token')}`,
};

// Clients API
export const clientsApi = {
  getAll: () => axios.get(`${API}/clients`, { headers: getAuthHeader() }),
  getById: (id) => axios.get(`${API}/clients/${id}`, { headers: getAuthHeader() }),
  create: (data) => axios.post(`${API}/clients`, data, { headers: getAuthHeader() }),
  update: (id, data) => axios.put(`${API}/clients/${id}`, data, { headers: getAuthHeader() }),
  delete: (id) => axios.delete(`${API}/clients/${id}`, { headers: getAuthHeader() }),
  setAssignedWorkers: (id, workerIds) =>
    axios.patch(`${API}/clients/${id}/assigned-workers`, { worker_ids: workerIds }, { headers: getAuthHeader() }),
};

// Templates API
export const templatesApi = {
  getAll: (type) => axios.get(`${API}/templates${type ? `?template_type=${type}` : ''}`, { headers: getAuthHeader() }),
  getById: (id) => axios.get(`${API}/templates/${id}`, { headers: getAuthHeader() }),
  create: (data) => axios.post(`${API}/templates`, data, { headers: getAuthHeader() }),
  update: (id, data) => axios.put(`${API}/templates/${id}`, data, { headers: getAuthHeader() }),
  delete: (id) => axios.delete(`${API}/templates/${id}`, { headers: getAuthHeader() }),
  seedDefaults: () => axios.post(`${API}/admin/seed-templates`, {}, { headers: getAuthHeader() }),
};

// Shifts API
export const shiftsApi = {
  getAll: (params) => axios.get(`${API}/shifts`, { headers: getAuthHeader(), params }),
  getById: (id) => axios.get(`${API}/shifts/${id}`, { headers: getAuthHeader() }),
  create: (data) => axios.post(`${API}/shifts`, data, { headers: getAuthHeader() }),
  complete: (id) => axios.put(`${API}/shifts/${id}/complete`, {}, { headers: getAuthHeader() }),
};

// Reports API
export const reportsApi = {
  getAll: (params) => axios.get(`${API}/reports`, { headers: getAuthHeader(), params }),
  getById: (id) => axios.get(`${API}/reports/${id}`, { headers: getAuthHeader() }),
  create: (data) => axios.post(`${API}/reports`, data, { headers: getAuthHeader() }),
  update: (id, data) => axios.put(`${API}/reports/${id}`, data, { headers: getAuthHeader() }),
  generateAI: (id) => axios.post(`${API}/reports/${id}/generate-ai`, {}, { headers: getAuthHeader() }),
  submit: (id, notifyEmail) => axios.put(`${API}/reports/${id}/submit${notifyEmail ? `?notify_email=${encodeURIComponent(notifyEmail)}` : ''}`, {}, { headers: getAuthHeader() }),
  downloadPdf: (id) => `${API}/reports/${id}/pdf?token=${localStorage.getItem('token')}`,
  getPdfBlob: async (id) => {
    const response = await axios.get(`${API}/reports/${id}/pdf`, {
      headers: getAuthHeader(),
      responseType: 'blob'
    });
    return response.data;
  },
  savePdf: (id) => axios.post(`${API}/reports/${id}/save-pdf`, {}, { headers: getAuthHeader() }),
  getSavedPdf: (id) => axios.get(`${API}/reports/${id}/saved-pdf`, { headers: getAuthHeader(), responseType: 'blob' }),
  listSavedPdfs: () => axios.get(`${API}/saved-pdfs`, { headers: getAuthHeader() }),
  // Version history
  getVersions: (id) => axios.get(`${API}/reports/${id}/versions`, { headers: getAuthHeader() }),
  getVersion: (id, version) => axios.get(`${API}/reports/${id}/versions/${version}`, { headers: getAuthHeader() }),
  restoreVersion: (id, version) => axios.post(`${API}/reports/${id}/restore/${version}`, {}, { headers: getAuthHeader() }),
  // Bulk export
  bulkExportPdf: async (reportIds) => {
    const response = await axios.post(`${API}/reports/bulk-pdf`, { report_ids: reportIds }, {
      headers: getAuthHeader(),
      responseType: 'blob'
    });
    return response.data;
  },
  // Consolidated (merged) PDF - one continuous PDF file
  consolidatedPdf: async (reportIds, includeCover = true, saveToArchive = false, bundleNote = null) => {
    const response = await axios.post(`${API}/reports/consolidated-pdf`, {
      report_ids: reportIds,
      include_cover: includeCover,
      save_to_archive: saveToArchive,
      bundle_note: bundleNote,
    }, {
      headers: getAuthHeader(),
      responseType: 'blob'
    });
    return response.data;
  },
  // Unified narrative PDF - one document aggregating all reports by section
  unifiedPdf: async (reportIds, saveToArchive = false, bundleNote = null, includeSummary = true) => {
    const response = await axios.post(`${API}/reports/unified-pdf`, {
      report_ids: reportIds,
      save_to_archive: saveToArchive,
      bundle_note: bundleNote,
      include_summary: includeSummary,
    }, {
      headers: getAuthHeader(),
      responseType: 'blob'
    });
    return response.data;
  },
  // Saved consolidated bundles
  listBundles: (clientId) => axios.get(`${API}/report-bundles${clientId ? `?client_id=${clientId}` : ''}`, { headers: getAuthHeader() }),
  downloadBundle: async (bundleId) => {
    const response = await axios.get(`${API}/report-bundles/${bundleId}/download`, {
      headers: getAuthHeader(),
      responseType: 'blob'
    });
    return response.data;
  },
  deleteBundle: (bundleId) => axios.delete(`${API}/report-bundles/${bundleId}`, { headers: getAuthHeader() }),
  emailBundle: (bundleId, payload) => axios.post(`${API}/report-bundles/${bundleId}/email`, payload, { headers: getAuthHeader() }),
  regenerateBundleSummary: (bundleId, payload) => axios.post(`${API}/report-bundles/${bundleId}/regenerate-summary`, payload, { headers: getAuthHeader() }),
  autoGenerateNow: () => axios.post(`${API}/report-bundles/auto-generate-now`, {}, { headers: getAuthHeader() })
};

// Handovers API
export const handoversApi = {
  getAll: (clientId) => axios.get(`${API}/handovers${clientId ? `?client_id=${clientId}` : ''}`, { headers: getAuthHeader() }),
  getById: (id) => axios.get(`${API}/handovers/${id}`, { headers: getAuthHeader() }),
  create: (data) => axios.post(`${API}/handovers`, data, { headers: getAuthHeader() }),
};

// Mental Health API
export const mentalHealthApi = {
  checkIn: (data) => axios.post(`${API}/mental-health/checkin`, data, { headers: getAuthHeader() }),
  getCheckins: () => axios.get(`${API}/mental-health/checkins`, { headers: getAuthHeader() }),
  getTips: () => axios.get(`${API}/mental-health/tips`, { headers: getAuthHeader() }),
};

// Compliance API
export const complianceApi = {
  getMetrics: () => axios.get(`${API}/compliance/metrics`, { headers: getAuthHeader() }),
};

// Voice to Text API
export const voiceApi = {
  transcribe: (audioBlob) => {
    const formData = new FormData();
    formData.append('file', audioBlob, 'recording.webm');
    return axios.post(`${API}/voice-to-text`, formData, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
    });
  }
};

// Admin API
export const adminApi = {
  getStats: () => axios.get(`${API}/admin/stats`, { headers: getAuthHeader() }),
  getUsers: () => axios.get(`${API}/admin/users`, { headers: getAuthHeader() }),
  createUser: (data) => axios.post(`${API}/admin/users`, data, { headers: getAuthHeader() }),
  updateUserRole: (userId, role) => axios.put(`${API}/admin/users/${userId}/role?role=${role}`, {}, { headers: getAuthHeader() }),
  deleteUser: (userId) => axios.delete(`${API}/admin/users/${userId}`, { headers: getAuthHeader() }),
  sendDigest: () => axios.post(`${API}/admin/send-digest`, {}, { headers: getAuthHeader() }),
  getActiveShifts: () => axios.get(`${API}/admin/active-shifts`, { headers: getAuthHeader() }),
};

// Supervisor API
export const supervisorApi = {
  getTeamWorkers: () => axios.get(`${API}/supervisor/team-workers`, { headers: getAuthHeader() }),
  getPendingReviews: () => axios.get(`${API}/supervisor/pending-reviews`, { headers: getAuthHeader() }),
  reviewReport: (reportId, action, feedback) => 
    axios.put(`${API}/supervisor/reports/${reportId}/review?action=${action}${feedback ? `&feedback=${encodeURIComponent(feedback)}` : ''}`, {}, { headers: getAuthHeader() }),
  getTeamStats: () => axios.get(`${API}/supervisor/team-stats`, { headers: getAuthHeader() }),
  getNotifications: (unreadOnly = false) => axios.get(`${API}/supervisor/notifications?unread_only=${unreadOnly}`, { headers: getAuthHeader() }),
  markNotificationRead: (id) => axios.post(`${API}/supervisor/notifications/${id}/read`, {}, { headers: getAuthHeader() }),
  markAllRead: () => axios.post(`${API}/supervisor/notifications/read-all`, {}, { headers: getAuthHeader() }),
  getShiftRequests: (status) => axios.get(`${API}/supervisor/shift-requests${status ? `?status=${status}` : ''}`, { headers: getAuthHeader() }),
  updateShiftRequest: (id, action) => axios.put(`${API}/supervisor/shift-requests/${id}?action=${action}`, {}, { headers: getAuthHeader() }),
  getMeetingRequests: (status) => axios.get(`${API}/supervisor/meeting-requests${status ? `?status=${status}` : ''}`, { headers: getAuthHeader() }),
  updateMeetingRequest: (id, action) => axios.put(`${API}/supervisor/meeting-requests/${id}?action=${action}`, {}, { headers: getAuthHeader() }),
};

// Analytics API
export const analyticsApi = {
  getOverview: (days = 30) => axios.get(`${API}/analytics/overview?days=${days}`, { headers: getAuthHeader() }),
  getReportsOverTime: (days = 30) => axios.get(`${API}/analytics/reports-over-time?days=${days}`, { headers: getAuthHeader() }),
  getComplianceOverTime: (days = 30) => axios.get(`${API}/analytics/compliance-over-time?days=${days}`, { headers: getAuthHeader() }),
  getWorkerPerformance: (days = 30) => axios.get(`${API}/analytics/worker-performance?days=${days}`, { headers: getAuthHeader() }),
  getTemplateUsage: () => axios.get(`${API}/analytics/template-usage`, { headers: getAuthHeader() }),
  getWellbeingSummary: (days = 30) => axios.get(`${API}/analytics/wellbeing-summary?days=${days}`, { headers: getAuthHeader() }),
  getClockSummary: (days = 30) => axios.get(`${API}/analytics/clock-summary?days=${days}`, { headers: getAuthHeader() }),
  generateCustomReport: (params) => {
    const q = new URLSearchParams({ metrics: params.metrics, group_by: params.group_by, days: params.days });
    if (params.worker_id) q.set('worker_id', params.worker_id);
    if (params.client_id) q.set('client_id', params.client_id);
    return axios.post(`${API}/analytics/custom-report?${q.toString()}`, {}, { headers: getAuthHeader() });
  },
};

// Audit API
export const auditApi = {
  getTrail: (params = {}) => axios.get(`${API}/audit/trail`, { headers: getAuthHeader(), params }),
  getEntityTrail: (entityType, entityId) => axios.get(`${API}/audit/trail/${entityType}/${entityId}`, { headers: getAuthHeader() }),
};

// Chat API
export const chatApi = {
  getChannels: () => axios.get(`${API}/chat/channels`, { headers: getAuthHeader() }),
  createChannel: (data) => axios.post(`${API}/chat/channels`, data, { headers: getAuthHeader() }),
  updateChannel: (id, data) => axios.put(`${API}/chat/channels/${id}`, data, { headers: getAuthHeader() }),
  deleteChannel: (id) => axios.delete(`${API}/chat/channels/${id}`, { headers: getAuthHeader() }),
  getMessages: (channel, before) => axios.get(`${API}/chat/messages?channel=${channel}${before ? `&before=${before}` : ''}`, { headers: getAuthHeader() }),
  sendMessage: (channel, text) => axios.post(`${API}/chat/messages?channel=${encodeURIComponent(channel)}&text=${encodeURIComponent(text)}`, {}, { headers: getAuthHeader() }),
  getUnreadCount: () => axios.get(`${API}/chat/messages/unread-count`, { headers: getAuthHeader() }),
  markRead: (channel) => axios.post(`${API}/chat/read?channel=${channel}`, {}, { headers: getAuthHeader() }),
  getReadStatus: () => axios.get(`${API}/chat/read-status`, { headers: getAuthHeader() }),
  setTyping: (channel, isTyping) => axios.post(`${API}/chat/typing`, { channel, is_typing: isTyping }, { headers: getAuthHeader() }),
  getTyping: (channel) => axios.get(`${API}/chat/typing?channel=${channel}`, { headers: getAuthHeader() }),
  uploadFile: (channel, text, file) => {
    const fd = new FormData();
    fd.append('channel', channel);
    fd.append('text', text || '');
    fd.append('file', file);
    return axios.post(`${API}/chat/upload`, fd, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } });
  },
  getFileUrl: (messageId) => `${API}/chat/file/${messageId}`,
  search: (q, channel) => axios.get(`${API}/chat/search?q=${encodeURIComponent(q)}${channel ? `&channel=${channel}` : ''}`, { headers: getAuthHeader() }),
  getMentionableUsers: (q) => axios.get(`${API}/chat/mentionable-users${q ? `?q=${encodeURIComponent(q)}` : ''}`, { headers: getAuthHeader() }),
};

// NDIS API
export const ndisApi = {
  getExportSummary: (days = 30) => axios.get(`${API}/ndis/export/summary?days=${days}`, { headers: getAuthHeader() }),
  exportCsv: (params = {}) => {
    const queryParts = [];
    if (params.status) queryParts.push(`status=${params.status}`);
    if (params.date_from) queryParts.push(`date_from=${params.date_from}`);
    if (params.date_to) queryParts.push(`date_to=${params.date_to}`);
    const queryStr = queryParts.length ? `?${queryParts.join('&')}` : '';
    return axios.get(`${API}/ndis/export/csv${queryStr}`, { headers: getAuthHeader(), responseType: 'blob' });
  },
};


// Participant API
export const participantApi = {
  getDashboard: () => axios.get(`${API}/participant/dashboard`, { headers: getAuthHeader() }),
  getReports: (skip = 0) => axios.get(`${API}/participant/reports?skip=${skip}`, { headers: getAuthHeader() }),
  getReport: (id) => axios.get(`${API}/participant/reports/${id}`, { headers: getAuthHeader() }),
  addComment: (reportId, text) => axios.post(`${API}/participant/comments`, { report_id: reportId, text }, { headers: getAuthHeader() }),
  getComments: (reportId) => axios.get(`${API}/participant/comments/${reportId}`, { headers: getAuthHeader() }),
  addProgress: (data) => axios.post(`${API}/participant/progress`, data, { headers: getAuthHeader() }),
  getProgress: (category) => axios.get(`${API}/participant/progress${category ? `?category=${category}` : ''}`, { headers: getAuthHeader() }),
  getProgressSummary: () => axios.get(`${API}/participant/progress/summary`, { headers: getAuthHeader() }),
  createShiftRequest: (data) => axios.post(`${API}/participant/shift-requests`, data, { headers: getAuthHeader() }),
  getShiftRequests: () => axios.get(`${API}/participant/shift-requests`, { headers: getAuthHeader() }),
  createMeetingRequest: (data) => axios.post(`${API}/participant/meeting-requests`, data, { headers: getAuthHeader() }),
  getMeetingRequests: () => axios.get(`${API}/participant/meeting-requests`, { headers: getAuthHeader() }),
  // Goals
  createGoal: (data) => axios.post(`${API}/participant/goals`, data, { headers: getAuthHeader() }),
  getGoals: (status, category) => {
    const params = [];
    if (status) params.push(`status=${status}`);
    if (category) params.push(`category=${category}`);
    return axios.get(`${API}/participant/goals${params.length ? '?' + params.join('&') : ''}`, { headers: getAuthHeader() });
  },
  updateGoal: (id, data) => axios.put(`${API}/participant/goals/${id}`, data, { headers: getAuthHeader() }),
  deleteGoal: (id) => axios.delete(`${API}/participant/goals/${id}`, { headers: getAuthHeader() }),
  getGoalIdeas: () => axios.get(`${API}/participant/goal-ideas`, { headers: getAuthHeader() }),
  linkToClient: (userId, clientId) => axios.post(`${API}/participant/link?user_id=${userId}&client_id=${clientId}`, {}, { headers: getAuthHeader() }),
  getLinks: () => axios.get(`${API}/participant/links`, { headers: getAuthHeader() }),
  // Behaviour insights (participant-scoped)
  getBehaviourSummary: (days = 90) => axios.get(`${API}/participant/behaviours/summary?days=${days}`, { headers: getAuthHeader() }),
  getBehaviourComparison: () => axios.get(`${API}/participant/behaviours/comparison`, { headers: getAuthHeader() }),
  getBehaviourRecords: (days = 60) => axios.get(`${API}/participant/behaviours/records?days=${days}`, { headers: getAuthHeader() }),
  // Celebrations
  celebrate: (data) => axios.post(`${API}/participant/celebrate`, data, { headers: getAuthHeader() }),
  getCelebrations: () => axios.get(`${API}/participant/celebrations`, { headers: getAuthHeader() }),
  getWorkerCelebrations: () => axios.get(`${API}/participant/worker/celebrations`, { headers: getAuthHeader() }),
  // Admin / Organisation overview
  getAdminOverview: () => axios.get(`${API}/participant/admin/overview`, { headers: getAuthHeader() }),
  getAdminParticipantDetail: (userId) => axios.get(`${API}/participant/admin/participant/${userId}`, { headers: getAuthHeader() }),
};


// Behaviours API
export const behavioursApi = {
  analyseReport: (reportId) => axios.post(`${API}/behaviours/analyse-report/${reportId}`, {}, { headers: getAuthHeader() }),
  analyseAll: (clientId) => axios.post(`${API}/behaviours/analyse-all/${clientId}`, {}, { headers: getAuthHeader() }),
  getClientBehaviours: (clientId, days = 90) => axios.get(`${API}/behaviours/client/${clientId}?days=${days}`, { headers: getAuthHeader() }),
  getClientSummary: (clientId, days = 90) => axios.get(`${API}/behaviours/client/${clientId}/summary?days=${days}`, { headers: getAuthHeader() }),
  getClientComparison: (clientId) => axios.get(`${API}/behaviours/client/${clientId}/comparison`, { headers: getAuthHeader() }),
  generateAiScores: (clientId) => axios.post(`${API}/behaviours/ai-progress-score/${clientId}`, {}, { headers: getAuthHeader() }),
  getCategories: () => axios.get(`${API}/behaviours/categories`, { headers: getAuthHeader() }),
  addCustomBehaviour: (data) => axios.post(`${API}/behaviours/custom`, data, { headers: getAuthHeader() }),
  deleteCustomBehaviour: (id) => axios.delete(`${API}/behaviours/custom/${id}`, { headers: getAuthHeader() }),
};


export const rosterApi = {
  listMissed: (hours = 24) => axios.get(`${API}/roster/missed-shifts?hours=${hours}`, { headers: getAuthHeader() }),
  checkMissedNow: () => axios.post(`${API}/roster/check-missed`, {}, { headers: getAuthHeader() }),
  listShifts: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') q.set(k, v); });
    const qs = q.toString();
    return axios.get(`${API}/roster/shifts${qs ? '?' + qs : ''}`, { headers: getAuthHeader() });
  },
  openShifts: () => axios.get(`${API}/roster/shifts/open`, { headers: getAuthHeader() }),
  getShift: (id) => axios.get(`${API}/roster/shifts/${id}`, { headers: getAuthHeader() }),
  createShift: (data) => axios.post(`${API}/roster/shifts`, data, { headers: getAuthHeader() }),
  updateShift: (id, data) => axios.put(`${API}/roster/shifts/${id}`, data, { headers: getAuthHeader() }),
  deleteShift: (id, applyToGroup = false) => axios.delete(`${API}/roster/shifts/${id}${applyToGroup ? '?apply_to_group=true' : ''}`, { headers: getAuthHeader() }),
  addWorkerToShift: (id, workerId) => axios.post(`${API}/roster/shifts/${id}/add-worker?worker_id=${encodeURIComponent(workerId)}`, {}, { headers: getAuthHeader() }),
  resolveShift: (id, body) => axios.post(`${API}/roster/shifts/${id}/resolve`, body, { headers: getAuthHeader() }),
  acceptShift: (id) => axios.post(`${API}/roster/shifts/${id}/accept`, {}, { headers: getAuthHeader() }),
  declineShift: (id, reason) => axios.post(`${API}/roster/shifts/${id}/decline${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`, {}, { headers: getAuthHeader() }),
  claimShift: (id) => axios.post(`${API}/roster/shifts/${id}/claim`, {}, { headers: getAuthHeader() }),
  clockInFromShift: (id) => axios.post(`${API}/roster/shifts/${id}/clock-in`, {}, { headers: getAuthHeader() }),
  createRecurring: (data) => axios.post(`${API}/roster/shifts/recurring`, data, { headers: getAuthHeader() }),
  getMyAvailability: () => axios.get(`${API}/roster/availability`, { headers: getAuthHeader() }),
  setMyAvailability: (slots) => axios.put(`${API}/roster/availability`, { slots }, { headers: getAuthHeader() }),
  getWorkerAvailability: (workerId) => axios.get(`${API}/roster/worker/${workerId}/availability`, { headers: getAuthHeader() }),
  getStats: () => axios.get(`${API}/roster/stats`, { headers: getAuthHeader() }),
  autoFill: (dryRun = false) => axios.post(`${API}/roster/shifts/auto-fill?dry_run=${dryRun}`, {}, { headers: getAuthHeader() }),
  copyWeek: (sourceWeekStart, targetWeekStart) =>
    axios.post(`${API}/roster/shifts/copy-week`,
      { source_week_start: sourceWeekStart, target_week_start: targetWeekStart },
      { headers: getAuthHeader() }),
};

// Organisations API (multi-tenant SaaS)
export const orgsApi = {
  getMine: () => axios.get(`${API}/organisations/me`, { headers: getAuthHeader() }),
  updateMine: (data) => axios.patch(`${API}/organisations/me`, data, { headers: getAuthHeader() }),
  myStats: () => axios.get(`${API}/organisations/me/stats`, { headers: getAuthHeader() }),
  listInvitations: () => axios.get(`${API}/organisations/invitations`, { headers: getAuthHeader() }),
  createInvitation: (data) => axios.post(`${API}/organisations/invitations`, data, { headers: getAuthHeader() }),
  revokeInvitation: (id) => axios.delete(`${API}/organisations/invitations/${id}`, { headers: getAuthHeader() }),
  validateInvitation: (token) => axios.get(`${API}/organisations/invitations/validate/${token}`),
  signup: (data) => axios.post(`${API}/organisations/signup`, data),
  // Public lead funnel
  submitAccessRequest: (data) => axios.post(`${API}/organisations/access-requests`, data),
  listAccessRequests: (status) => axios.get(`${API}/organisations/access-requests${status ? `?status=${status}` : ''}`, { headers: getAuthHeader() }),
  approveAccessRequest: (id) => axios.post(`${API}/organisations/access-requests/${id}/approve`, {}, { headers: getAuthHeader() }),
  declineAccessRequest: (id) => axios.post(`${API}/organisations/access-requests/${id}/decline`, {}, { headers: getAuthHeader() }),
};

export const manualEntriesApi = {
  create: (data) => axios.post(`${API}/timesheets/manual`, data, { headers: getAuthHeader() }),
  list: (status) => axios.get(`${API}/timesheets/manual${status ? `?status=${status}` : ''}`, { headers: getAuthHeader() }),
  pendingCount: () => axios.get(`${API}/timesheets/manual/pending-count`, { headers: getAuthHeader() }),
  approve: (id) => axios.post(`${API}/timesheets/manual/${id}/approve`, {}, { headers: getAuthHeader() }),
  reject: (id, reason) => axios.post(`${API}/timesheets/manual/${id}/reject`, { reason }, { headers: getAuthHeader() }),
  remove: (id) => axios.delete(`${API}/timesheets/manual/${id}`, { headers: getAuthHeader() }),
};
manualEntriesApi.sendReminders = () => axios.post(`${API}/timesheets/manual/send-reminders`, {}, { headers: getAuthHeader() });
manualEntriesApi.remindersStatus = () => axios.get(`${API}/timesheets/manual/reminders/status`, { headers: getAuthHeader() });
